import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { api, money, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { BrandLogo } from "@/components/BrandLogo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Search, Plus, Minus, X, Banknote, CreditCard, Landmark, LogOut, Clock, ArrowLeft, Printer, PauseCircle, Percent, User, Package } from "lucide-react";
import { printReceipt } from "@/lib/receiptPrinter";
import { ShareDocButton } from "@/components/ShareDocButton";
import { isNetworkError, enqueueSale, getQueue, syncQueue, cacheCatalog, readCatalog } from "@/lib/offlinePos";
import { WifiOff, RefreshCw } from "lucide-react";

const PAYMENTS = [
  { v: "cash", label: "Cash", icon: Banknote },
  { v: "card", label: "Card", icon: CreditCard },
  { v: "eft", label: "EFT", icon: Landmark },
];
const gasProducts = () => api.get("/products", { params: { gas: true, status: "Active" } });

export default function POS() {
  const { user, can } = useAuth();
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [customerId, setCustomerId] = useState("");
  const [tier, setTier] = useState("Retail");
  const [search, setSearch] = useState("");
  const [cart, setCart] = useState([]);
  const [shift, setShift] = useState(undefined);
  const [floatOpen, setFloatOpen] = useState(false);
  const [floatAmt, setFloatAmt] = useState("");
  const [payMethod, setPayMethod] = useState("cash");
  const [split, setSplit] = useState(null); // {cash, card, eft} strings or null
  const [txnType, setTxnType] = useState("exchange");
  const [discount, setDiscount] = useState(0);
  const [discOpen, setDiscOpen] = useState(false);
  const [discDraft, setDiscDraft] = useState({ pct: "", pin: "" });
  const [holds, setHolds] = useState([]);
  const [holdsOpen, setHoldsOpen] = useState(false);
  const [receipt, setReceipt] = useState(null);
  const [closeOpen, setCloseOpen] = useState(false);
  const [counted, setCounted] = useState("");
  const [closeSummary, setCloseSummary] = useState(null);
  const priceReq = useRef(0);
  const [online, setOnline] = useState(navigator.onLine);
  const [queued, setQueued] = useState(getQueue().length);
  const [syncingNow, setSyncingNow] = useState(false);

  const runSync = async () => {
    if (!getQueue().length) return;
    setSyncingNow(true);
    try {
      const r = await syncQueue();
      if (r) {
        setQueued(getQueue().length);
        if (r.synced) toast.success(`${r.synced} offline sale(s) synced`);
        if (r.failed.length) toast.error(`${r.failed.length} offline sale(s) could not sync: ${r.failed[0].detail}`);
        gasProducts().then(({ data }) => setProducts(data)).catch(() => {});
      }
    } catch { /* still offline */ } finally { setSyncingNow(false); }
  };
  useEffect(() => {
    const up = () => { setOnline(true); runSync(); };
    const down = () => setOnline(false);
    window.addEventListener("online", up); window.addEventListener("offline", down);
    const iv = setInterval(() => { if (navigator.onLine) runSync(); }, 30000);
    return () => { window.removeEventListener("online", up); window.removeEventListener("offline", down); clearInterval(iv); };
  }, []); // eslint-disable-line

  const allowed = can("pos");
  const loadShift = () => api.get("/pos/shift/current").then(({ data }) => setShift(data)).catch(() => setShift(null));
  const loadHolds = () => api.get("/pos/holds").then(({ data }) => setHolds(data)).catch(() => {});
  useEffect(() => {
    if (!allowed) return;
    const cached = readCatalog();
    if (cached) { setProducts(cached.products || []); setCustomers(cached.customers || []); }
    Promise.all([gasProducts(), api.get("/customers")]).then(([p, c]) => { setProducts(p.data); setCustomers(c.data); cacheCatalog(p.data, c.data); }).catch(() => {});
    loadShift(); loadHolds(); runSync();
  }, [allowed]); // eslint-disable-line

  // re-price cart from tiers / qty breaks / promotions whenever items or customer change
  const cartKey = cart.map((i) => `${i.product_id}:${i.quantity}`).join("|");
  useEffect(() => {
    if (!cart.length) {
      if (!customerId) { setTier("Retail"); return; }
      api.post("/pricing/quote", { customer_id: customerId, items: [] }).then(({ data }) => setTier(data.tier)).catch(() => {});
      return;
    }
    const n = ++priceReq.current;
    api.post("/pricing/quote", { customer_id: customerId || null, items: cart.map((i) => ({ product_id: i.product_id, quantity: i.quantity })) })
      .then(({ data }) => {
        if (n !== priceReq.current) return;
        setTier(data.tier);
        setCart((c) => c.map((it) => { const p = data.items.find((x) => x.product_id === it.product_id); return p ? { ...it, unit_price: p.unit_price, price_source: p.price_source } : it; }));
      }).catch(() => {});
  }, [cartKey, customerId]); // eslint-disable-line

  if (allowed === false)
    return <div className="h-screen flex items-center justify-center text-muted-foreground">You do not have POS access.</div>;

  const filtered = products.filter((p) => p.name.toLowerCase().includes(search.toLowerCase()) || (p.sku || "").toLowerCase().includes(search.toLowerCase()));
  const addToCart = (p) => setCart((c) => {
    const ex = c.find((x) => x.product_id === p.id);
    if (ex) return c.map((x) => (x.product_id === p.id ? { ...x, quantity: x.quantity + 1 } : x));
    return [...c, { product_id: p.id, name: p.name, unit_price: p.selling_price, quantity: 1 }];
  });
  const setQty = (pid, q) => setCart((c) => c.map((x) => (x.product_id === pid ? { ...x, quantity: Math.max(0.5, q) } : x)));
  const removeItem = (pid) => setCart((c) => c.filter((x) => x.product_id !== pid));

  const subtotal = cart.reduce((s, i) => s + i.quantity * i.unit_price, 0);
  const discountAmt = subtotal * discount / 100;
  const total = Math.round((subtotal - discountAmt) * 100) / 100;
  const splitSum = split ? Object.values(split).reduce((s, v) => s + (Number(v) || 0), 0) : total;
  const customer = customers.find((c) => c.id === customerId);

  const scan = async () => {
    const code = search.trim();
    if (!code) return;
    if (!shift) return toast.error("Open a shift first");
    let p = products.find((x) => x.barcode === code || (x.sku || "").toLowerCase() === code.toLowerCase());
    if (!p) {
      try { const { data } = await api.get(`/products/barcode/${encodeURIComponent(code)}`); p = data; }
      catch { return toast.error("No product for that barcode"); }
    }
    addToCart(p); setSearch(""); toast.success(`Added ${p.name}`);
  };

  const openShift = async () => {
    try { const { data } = await api.post("/pos/shift/open", { opening_float: Number(floatAmt) || 0 }); setShift(data); setFloatOpen(false); toast.success("Shift opened"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not open shift"); loadShift(); }
  };
  const closeShift = async () => {
    try {
      const { data } = await api.post("/pos/shift/close-z", { counted_cash: counted === "" ? null : Number(counted) });
      setCloseSummary(data); setCloseOpen(false); setShift(null); setCounted("");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not close shift"); loadShift(); }
  };

  const applyDiscount = async () => {
    const pct = Number(discDraft.pct);
    if (!(pct > 0 && pct <= 100)) return toast.error("Enter a discount between 1 and 100%");
    if (!navigator.onLine) return toast.error("Discounts need a connection to verify the manager PIN");
    try { await api.post("/pos/verify-pin", { pin: discDraft.pin }); setDiscount(pct); setDiscOpen(false); toast.success(`${pct}% discount authorised`); }
    catch (e) { toast.error(isNetworkError(e) ? "Discounts need a connection to verify the manager PIN" : formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const resetSale = () => { setCart([]); setDiscount(0); setSplit(null); setDiscDraft({ pct: "", pin: "" }); setCustomerId(""); setTier("Retail"); };

  const holdSale = async () => {
    if (!cart.length) return toast.error("Cart is empty");
    try {
      await api.post("/pos/holds", { items: cart, customer_id: customerId || null, customer_name: customer?.company_name, transaction_type: txnType, discount_pct: discount, label: customer?.company_name || `Hold ${new Date().toLocaleTimeString("en-ZA", { hour: "2-digit", minute: "2-digit" })}` });
      toast.success(discount > 0 ? "Sale parked — discount will need manager re-authorisation on resume" : "Sale parked"); resetSale(); loadHolds();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const resumeHold = async (h) => {
    if (cart.length) return toast.error("Finish or park the current sale first");
    setCart(h.items); setCustomerId(h.customer_id || ""); setTxnType(h.transaction_type || "exchange");
    if (h.discount_pct > 0) { setDiscDraft({ pct: String(h.discount_pct), pin: "" }); toast.info(`This sale had a ${h.discount_pct}% discount — re-enter the manager PIN to apply it`); }
    await api.delete(`/pos/holds/${h.id}`).catch(() => {});
    setHoldsOpen(false); loadHolds();
  };

  const checkout = async () => {
    if (cart.length === 0) return toast.error("Cart is empty");
    const payments = split
      ? Object.entries(split).filter(([, v]) => Number(v) > 0).map(([method, v]) => ({ method, amount: Number(v) }))
      : [{ method: payMethod, amount: total }];
    if (Math.abs(payments.reduce((s, p) => s + p.amount, 0) - total) > 0.01) return toast.error("Split payments must add up to the total");
    const payload = {
      items: cart.map((i) => ({ ...i, transaction_type: txnType })), payments, transaction_type: txnType,
      discount_pct: discount, manager_pin: discount > 0 ? discDraft.pin : undefined,
      customer_id: customerId || null, customer_name: customer?.company_name || null, price_tier: tier,
    };
    const queueOffline = () => {
      if (discount > 0) return toast.error("Discounts need the server to verify the manager PIN — remove the discount or wait for connection");
      const entry = enqueueSale({ ...payload, subtotal, discount_amount: discountAmt, total, operator_name: user?.name, created_at: new Date().toISOString() });
      setQueued(getQueue().length);
      setReceipt(entry); resetSale();
      toast.warning("No connection — sale saved on this till and will sync automatically");
    };
    if (!navigator.onLine) return queueOffline();
    try {
      const { data } = await api.post("/pos/transactions", payload);
      setReceipt(data); resetSale();
      toast.success(`Sale ${data.number} completed`);
      gasProducts().then(({ data }) => setProducts(data)).catch(() => {});
    } catch (e) {
      if (isNetworkError(e)) return queueOffline();
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  return (
    <div className="h-screen flex flex-col bg-secondary/40 overflow-hidden">
      <header className="h-14 bg-black text-white flex items-center px-4 gap-4 shrink-0">
        <button onClick={() => navigate("/")} className="text-white/70 hover:text-white transition-colors" data-testid="pos-back"><ArrowLeft className="h-5 w-5" /></button>
        <BrandLogo light size="sm" />
        <span className="text-white/40 text-sm hidden sm:inline">Gas Exchange POS</span>
        <div className="ml-auto flex items-center gap-3 text-sm">
          {!online && <span className="flex items-center gap-1.5 bg-primary text-white text-xs font-semibold px-2.5 py-1 rounded-sm" data-testid="offline-banner"><WifiOff className="h-3.5 w-3.5" /> OFFLINE — sales are saved on this till</span>}
          {queued > 0 && <button onClick={runSync} className="flex items-center gap-1.5 bg-amber-500 text-black text-xs font-semibold px-2.5 py-1 rounded-sm" data-testid="queued-badge"><RefreshCw className={`h-3.5 w-3.5 ${syncingNow ? "animate-spin" : ""}`} /> {queued} queued {online ? "· tap to sync" : ""}</button>}
          <Button size="sm" variant="ghost" className="text-white hover:bg-white/10 h-8 hidden md:inline-flex" onClick={() => navigate("/products?section=gas")} data-testid="manage-gas-products-button"><Package className="h-4 w-4 mr-1" /> Gas Products</Button>
          <Button size="sm" variant="ghost" className="text-white hover:bg-white/10 h-8 relative" onClick={() => setHoldsOpen(true)} data-testid="holds-button"><PauseCircle className="h-4 w-4 mr-1" /> Parked{holds.length > 0 && <span className="ml-1.5 bg-primary text-white text-[10px] rounded-full px-1.5">{holds.length}</span>}</Button>
          {shift ? (<>
            <span className="flex items-center gap-1.5 text-white/70"><Clock className="h-3.5 w-3.5" /> Shift open</span>
            <span className="text-white/50">{user?.name}</span>
            <Button size="sm" variant="ghost" className="text-white hover:bg-white/10 h-8" onClick={() => setCloseOpen(true)} data-testid="close-shift-button"><LogOut className="h-4 w-4 mr-1" /> Cash-up</Button>
          </>) : shift === null ? (
            <Button size="sm" className="bg-primary hover:bg-[#C81219] h-8" onClick={() => setFloatOpen(true)} data-testid="open-shift-button">Open Shift</Button>
          ) : !online ? <span className="text-white/60 text-xs">Offline mode</span> : null}
        </div>
      </header>

      <div className="flex-1 flex overflow-hidden">
        <div className="flex-1 flex flex-col overflow-hidden">
          <div className="p-4 shrink-0">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input value={search} onChange={(e) => setSearch(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") scan(); }}
                placeholder="Search or scan cylinder barcode, then Enter..." className="pl-9 h-12 bg-white text-base" data-testid="pos-search" />
            </div>
          </div>
          <div className="flex-1 overflow-y-auto px-4 pb-4">
            <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-4 gap-3">
              {filtered.map((p) => (
                <button key={p.id} onClick={() => shift ? addToCart(p) : toast.error("Open a shift first")} data-testid={`pos-product-${p.sku}`}
                  className="group bg-white border border-border rounded-md p-4 text-left hover:border-primary hover:shadow-sm transition-all active:scale-[0.98]">
                  <div className="h-20 rounded-sm bg-secondary mb-3 flex items-center justify-center overflow-hidden">
                    <img src="https://images.unsplash.com/photo-1664396113489-e50bddd4a777?crop=entropy&cs=srgb&fm=jpg&q=85&w=300" alt="" className="h-full w-full object-cover" />
                  </div>
                  <div className="text-sm font-semibold leading-tight line-clamp-2">{p.name}</div>
                  <div className="mt-1.5 flex items-center justify-between">
                    <span className="font-display font-bold text-primary tabular">{money(p.selling_price)}</span>
                    <span className="text-[10px] text-muted-foreground">{p.stock_quantity} in stock</span>
                  </div>
                </button>
              ))}
              {filtered.length === 0 && <p className="col-span-full text-center text-muted-foreground py-12">No gas products found.</p>}
            </div>
          </div>
        </div>

        <aside className="w-[380px] shrink-0 bg-white border-l border-border flex flex-col">
          <div className="p-4 border-b border-border space-y-2">
            <div className="flex gap-1.5">
              {["exchange", "sale", "refill", "deposit"].map((t) => (
                <button key={t} onClick={() => setTxnType(t)} data-testid={`txn-type-${t}`}
                  className={`flex-1 py-1.5 text-xs font-medium rounded-sm capitalize transition-colors ${txnType === t ? "bg-black text-white" : "bg-secondary text-muted-foreground hover:bg-secondary/70"}`}>{t}</button>
              ))}
            </div>
            <div className="flex items-center gap-2">
              <User className="h-4 w-4 text-muted-foreground shrink-0" />
              <Select value={customerId || "__walkin"} onValueChange={(v) => setCustomerId(v === "__walkin" ? "" : v)}>
                <SelectTrigger className="h-8 text-xs" data-testid="pos-customer-select"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="__walkin">Walk-in customer</SelectItem>{customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.company_name}{c.price_tier && c.price_tier !== "Retail" ? ` · ${c.price_tier}` : ""}</SelectItem>)}</SelectContent>
              </Select>
              {tier !== "Retail" && <span className="text-[10px] font-semibold uppercase bg-black text-white rounded-sm px-1.5 py-0.5" data-testid="pos-tier-badge">{tier}</span>}
            </div>
          </div>

          <div className="flex-1 overflow-y-auto p-4 space-y-2">
            {cart.length === 0 ? (
              <div className="h-full flex flex-col items-center justify-center text-muted-foreground text-sm">
                <div className="h-12 w-12 rounded-full bg-secondary flex items-center justify-center mb-3"><Plus className="h-5 w-5" /></div>Tap a product to add
              </div>
            ) : cart.map((it) => (
              <div key={it.product_id} className="border border-border rounded-md p-3" data-testid={`cart-item-${it.product_id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1"><span className="text-sm font-medium leading-tight">{it.name}</span>{it.price_source && it.price_source !== "Retail" && <div className="text-[10px] text-primary font-medium">{it.price_source} · {money(it.unit_price)}</div>}</div>
                  <button onClick={() => removeItem(it.product_id)} className="text-muted-foreground hover:text-primary"><X className="h-4 w-4" /></button>
                </div>
                <div className="mt-2 flex items-center justify-between">
                  <div className="flex items-center gap-1.5">
                    <Button size="icon" variant="outline" className="h-7 w-7" onClick={() => setQty(it.product_id, it.quantity - (txnType === "refill" ? 0.5 : 1))}><Minus className="h-3 w-3" /></Button>
                    <Input value={it.quantity} onChange={(e) => setQty(it.product_id, Number(e.target.value) || 0)} className="h-7 w-14 text-center px-1" />
                    <Button size="icon" variant="outline" className="h-7 w-7" onClick={() => setQty(it.product_id, it.quantity + (txnType === "refill" ? 0.5 : 1))}><Plus className="h-3 w-3" /></Button>
                  </div>
                  <span className="font-semibold tabular">{money(it.quantity * it.unit_price)}</span>
                </div>
              </div>
            ))}
          </div>

          <div className="border-t border-border p-4 shrink-0">
            {discount > 0 && <div className="flex items-center justify-between text-xs mb-1 text-primary"><span>Discount {discount}% (manager approved)</span><span className="tabular">- {money(discountAmt)} <button onClick={() => setDiscount(0)} className="ml-1 underline">remove</button></span></div>}
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm text-muted-foreground">Total (incl. VAT)</span>
              <span className="font-display text-3xl font-black tabular" data-testid="pos-total">{money(total)}</span>
            </div>
            {split ? (
              <div className="space-y-1.5 mb-3" data-testid="split-tender">
                {PAYMENTS.map((p) => <div key={p.v} className="flex items-center gap-2"><p.icon className="h-4 w-4 text-muted-foreground" /><span className="text-xs w-10">{p.label}</span><Input type="number" value={split[p.v]} onChange={(e) => setSplit({ ...split, [p.v]: e.target.value })} className="h-8" placeholder="0.00" data-testid={`split-${p.v}`} /></div>)}
                <div className={`flex justify-between text-xs ${Math.abs(splitSum - total) > 0.01 ? "text-primary" : "text-green-700"}`}><span>Allocated</span><span className="tabular">{money(splitSum)} / {money(total)}{Math.abs(splitSum - total) > 0.01 && ` (${money(total - splitSum)} left)`}</span></div>
                <button onClick={() => setSplit(null)} className="text-xs underline text-muted-foreground">Single payment</button>
              </div>
            ) : (
              <div className="grid grid-cols-4 gap-2 mb-3">
                {PAYMENTS.map((p) => (
                  <button key={p.v} onClick={() => setPayMethod(p.v)} data-testid={`pay-${p.v}`}
                    className={`flex flex-col items-center gap-1 py-2.5 rounded-md border text-xs font-medium transition-colors ${payMethod === p.v ? "border-primary bg-primary/5 text-primary" : "border-border text-muted-foreground hover:bg-secondary"}`}>
                    <p.icon className="h-4 w-4" /> {p.label}
                  </button>
                ))}
                <button onClick={() => setSplit({ cash: "", card: "", eft: "" })} data-testid="pay-split" className="flex flex-col items-center gap-1 py-2.5 rounded-md border border-dashed border-border text-xs font-medium text-muted-foreground hover:bg-secondary"><Banknote className="h-4 w-4" />Split</button>
              </div>
            )}
            <div className="flex gap-2 mb-2">
              <Button variant="outline" className="flex-1 h-9" onClick={holdSale} disabled={!cart.length} data-testid="hold-sale-button"><PauseCircle className="h-4 w-4 mr-1" /> Park</Button>
              <Button variant="outline" className="flex-1 h-9" onClick={() => setDiscOpen(true)} disabled={!cart.length} data-testid="discount-button"><Percent className="h-4 w-4 mr-1" /> Discount</Button>
            </div>
            <Button onClick={checkout} disabled={(!shift && online) || cart.length === 0} className="w-full h-14 bg-primary hover:bg-[#C81219] text-white font-display font-bold text-lg transition-colors" data-testid="pos-checkout-button">{online ? "Complete Transaction" : "Save Sale Offline"}</Button>
          </div>
        </aside>
      </div>

      <Dialog open={floatOpen} onOpenChange={setFloatOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle className="font-display text-xl">Open Shift</DialogTitle></DialogHeader>
          <div className="space-y-2 py-2"><Label className="text-xs uppercase text-muted-foreground">Opening cash float</Label>
            <Input type="number" value={floatAmt} onChange={(e) => setFloatAmt(e.target.value)} placeholder="0.00" className="h-12 text-lg" data-testid="float-input" autoFocus /></div>
          <DialogFooter><Button onClick={openShift} className="w-full bg-primary hover:bg-[#C81219] text-white" data-testid="confirm-open-shift">Start Selling</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={discOpen} onOpenChange={setDiscOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle className="font-display text-xl">Manager discount</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3 py-2">
            <div className="space-y-1.5"><Label className="text-xs uppercase text-muted-foreground">Discount %</Label><Input type="number" value={discDraft.pct} onChange={(e) => setDiscDraft({ ...discDraft, pct: e.target.value })} data-testid="discount-pct-input" autoFocus /></div>
            <div className="space-y-1.5"><Label className="text-xs uppercase text-muted-foreground">Manager PIN</Label><Input type="password" inputMode="numeric" value={discDraft.pin} onChange={(e) => setDiscDraft({ ...discDraft, pin: e.target.value })} data-testid="discount-pin-input" /></div>
          </div>
          <DialogFooter><Button onClick={applyDiscount} className="w-full bg-black hover:bg-zinc-800 text-white" data-testid="apply-discount-button">Authorise</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={holdsOpen} onOpenChange={setHoldsOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="font-display text-xl">Parked sales</DialogTitle></DialogHeader>
          <div className="divide-y divide-border max-h-80 overflow-y-auto">
            {holds.map((h) => (
              <div key={h.id} className="flex items-center gap-3 py-2.5" data-testid={`hold-${h.id}`}>
                <div className="flex-1"><div className="font-medium text-sm">{h.label}</div><div className="text-xs text-muted-foreground">{h.items.length} item(s) · {h.operator_name} · {money(h.total)}</div></div>
                <Button size="sm" onClick={() => resumeHold(h)} className="bg-primary hover:bg-[#C81219] text-white h-8" data-testid={`resume-hold-${h.id}`}>Resume</Button>
                <Button size="icon" variant="ghost" className="h-8 w-8" onClick={async () => { await api.delete(`/pos/holds/${h.id}`); loadHolds(); }}><X className="h-4 w-4" /></Button>
              </div>
            ))}
            {holds.length === 0 && <p className="text-sm text-muted-foreground py-8 text-center">No parked sales.</p>}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={closeOpen} onOpenChange={setCloseOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle className="font-display text-xl">Close shift &amp; Z-report</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">Count the cash in the drawer (including the float). We'll compare it to the expected amount.</p>
          <div className="space-y-1.5"><Label className="text-xs uppercase text-muted-foreground">Counted cash</Label><Input type="number" value={counted} onChange={(e) => setCounted(e.target.value)} placeholder="0.00" className="h-12 text-lg" data-testid="counted-cash-input" autoFocus /></div>
          <DialogFooter><Button onClick={closeShift} className="w-full bg-primary hover:bg-[#C81219] text-white" data-testid="confirm-close-shift">Close &amp; Print Z-Report</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!receipt} onOpenChange={() => setReceipt(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle className="font-display text-xl">Receipt {receipt?.number} {receipt?.offline && <span className="ml-2 text-xs bg-amber-500 text-black px-1.5 py-0.5 rounded-sm align-middle" data-testid="receipt-offline-badge">saved offline</span>}</DialogTitle></DialogHeader>
          {receipt && (
            <div className="text-sm">
              <div className="text-center mb-3"><div className="font-display font-black text-lg">BOCALLY HOLDINGS</div><div className="text-xs text-muted-foreground">Gas Exchange &bull; {new Date().toLocaleString()}</div>{receipt.customer_name && <div className="text-xs">Customer: {receipt.customer_name}</div>}</div>
              <div className="border-y border-dashed border-border py-3 space-y-1.5">
                {receipt.items?.map((it, i) => <div key={i} className="flex justify-between"><span>{it.quantity} × {it.name}</span><span className="tabular">{money(it.quantity * it.unit_price)}</span></div>)}
              </div>
              {receipt.discount_amount > 0 && <div className="flex justify-between text-xs mt-2"><span>Discount {receipt.discount_pct}%</span><span className="tabular">- {money(receipt.discount_amount)}</span></div>}
              <div className="flex justify-between font-bold text-lg mt-3"><span>TOTAL</span><span className="tabular text-primary">{money(receipt.total)}</span></div>
              <div className="text-xs text-muted-foreground mt-1">{receipt.payments?.map((p) => `${p.method.toUpperCase()} ${money(p.amount)}`).join(" + ")} &bull; VAT incl {money(receipt.vat_amount)}</div>
              {receipt.bank_account && <div className="mt-3 border-t border-dashed border-border pt-2 text-xs" data-testid="receipt-bank"><div className="font-semibold">Banking Details</div><div>{receipt.bank_account.bank} · {receipt.bank_account.holder}</div><div>Acc {receipt.bank_account.account_number} · Branch {receipt.bank_account.branch_code}</div></div>}
            </div>
          )}
          <DialogFooter className="gap-2">
            {receipt?.id && <ShareDocButton size="default" shareUrl={`/pos/transactions/${receipt.id}/share-text`} emailUrl={`/pos/transactions/${receipt.id}/email`} defaultPhone={customers.find((c) => c.id === receipt.customer_id)?.phone || ""} defaultEmail={customers.find((c) => c.id === receipt.customer_id)?.email || ""} label="WhatsApp / Email" testId="share-receipt-button" />}
            <Button variant="outline" onClick={() => printReceipt(receipt)} data-testid="print-receipt"><Printer className="h-4 w-4 mr-1.5" /> Print Receipt</Button>
            <Button onClick={() => setReceipt(null)} className="bg-primary hover:bg-[#C81219] text-white" data-testid="close-receipt">New Sale</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!closeSummary} onOpenChange={() => setCloseSummary(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle className="font-display text-xl">Z-Report {closeSummary?.z_number}</DialogTitle></DialogHeader>
          {closeSummary && (
            <div className="text-sm space-y-2">
              <Line label="Opening Float" value={money(closeSummary.opening_float)} />
              <Line label="Cash Sales" value={money(closeSummary.summary?.cash)} />
              <Line label="Card Sales" value={money(closeSummary.summary?.card)} />
              <Line label="EFT Sales" value={money(closeSummary.summary?.eft)} />
              <Line label="Discounts" value={money(closeSummary.summary?.discounts)} />
              <Line label="Cash Refunds" value={money(closeSummary.cash_refunds)} />
              <Line label="Transactions" value={`${closeSummary.summary?.count} (${closeSummary.summary?.voided} voided)`} />
              <div className="border-t border-border pt-2 flex justify-between font-bold"><span>Total Sales</span><span className="tabular text-primary">{money(closeSummary.summary?.total)}</span></div>
              <Line label="Expected in Drawer" value={money(closeSummary.expected_cash)} />
              <Line label="Counted" value={money(closeSummary.counted_cash)} />
              <div className={`flex justify-between font-bold ${closeSummary.variance < 0 ? "text-primary" : closeSummary.variance > 0 ? "text-green-700" : ""}`}><span>Variance</span><span className="tabular" data-testid="cashup-variance">{closeSummary.variance > 0 ? "+" : ""}{money(closeSummary.variance)}</span></div>
            </div>
          )}
          <DialogFooter><Button onClick={() => setCloseSummary(null)} className="w-full bg-primary hover:bg-[#C81219] text-white" data-testid="cashup-done">Done</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Line({ label, value }) {
  return <div className="flex justify-between"><span className="text-muted-foreground">{label}</span><span className="tabular">{value}</span></div>;
}
